import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaSpinner,
  FaCheck,
  FaTimes,
  FaBuilding,
  FaUserPlus,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";

import InlineAlert from "../..//components/UI/DesignStandards/Notifications/InlineAlert";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { useOrganization } from "../../context/OrganizationContext";
import InviteSignupForm from "../../components/InviteSignupForm";
import { SkeletonBoard } from "../../components/UI/DesignStandards";

const InviteAccept = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshInvites } = useNotifications();
  const { refreshOrganizations } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState("");
  const [userStatus, setUserStatus] = useState(null);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  useEffect(() => {
    fetchInvite();
  }, [inviteId]);

  const fetchInvite = async () => {
    try {
      // Use the secure function to get invite by ID
      const { data: inviteData, error: inviteError } = await supabase.rpc(
        "get_invite_by_id",
        { invite_id: inviteId }
      );

      if (inviteError) {
        console.error("Database function error:", inviteError);
        throw inviteError;
      }

      // Check if invite exists and is valid
      if (!inviteData || inviteData.length === 0) {
        setError("Invitation not found or has expired");
        setLoading(false);
        return;
      }

      const invite = inviteData[0]; // Get the first (and only) result

      // Get the organization information
      const { data: organizationData, error: orgError } = await supabase
        .from("organizations")
        .select("id, name, nameAbbreviation, description")
        .eq("id", invite.organizationID)
        .single();

      if (orgError) {
        console.error("Error fetching organization:", orgError);
        setError("Failed to load organization information");
        setLoading(false);
        return;
      }

      // The inviter information is now included in the invite data from the function
      if (invite.inviterName) {
        invite.invitedByUser = {
          name: invite.inviterName,
          email: invite.inviterEmail,
        };
      }

      setInvite(invite);
      setOrganization(organizationData);

      // Check if user exists for this email
      await checkUserStatus(invite.email, invite.userExists);
    } catch (err) {
      console.error("Error fetching invite:", err);
      setError("Failed to load invitation");
    } finally {
      setLoading(false);
    }
  };

  const checkUserStatus = async (email, userExists) => {
    try {
      // If user is logged in and their email matches the invite, show accept/decline
      if (user && user.email.toLowerCase() === email.toLowerCase()) {
        setShowSignupForm(false);
        setUserStatus({ userExists: true, user: user });
        return;
      }

      // If user is not logged in, use the userExists flag from the database function
      if (!user) {
        if (userExists) {
          // User exists but is not logged in, show sign-in options
          setShowSignupForm(false);
          setUserStatus({ userExists: true, needsSignIn: true });
        } else {
          // User doesn't exist, show signup form
          setShowSignupForm(true);
          setUserStatus({ userExists: false });
        }
      } else {
        // User is logged in but email doesn't match - BLOCK ACCESS
        setError(
          "You don't have permission to view this invitation. This invitation was sent to a different email address."
        );
        setUserStatus({ userExists: false, accessDenied: true });
        return;
      }
    } catch (err) {
      console.error("Error checking user status:", err);
      // Fallback: assume user doesn't exist if we can't check
      setUserStatus({ userExists: false });
      if (!user) {
        setShowSignupForm(true);
      }
    }
  };

  const handleAcceptInvite = async () => {
    if (!user) {
      setActionError("You must be logged in to accept an invitation");
      navigate("/signin", { state: { from: `/invite/${inviteId}` } });
      return;
    }

    // Email verification is now handled server-side for security

    setAccepting(true);
    setActionError("");

    try {
      // Use the secure function to accept the invite
      const { data: result, error } = await supabase.rpc(
        "accept_organization_invite",
        {
          invite_id: inviteId,
        }
      );

      if (error) {
        console.error("Error accepting invite:", error);
        throw error;
      }

      if (!result || result.length === 0) {
        throw new Error("No result returned from accept function");
      }

      const acceptResult = result[0];
      if (!acceptResult.success) {
        throw new Error(acceptResult.message);
      }

      // Refresh organizations and notifications before navigating
      await refreshOrganizations();
      refreshInvites();

      navigate("/organization");
    } catch (error) {
      console.error("Error accepting invite:", error);
      setActionError("Failed to accept invitation. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineInvite = async () => {
    setAccepting(true);

    try {
      const { error } = await supabase
        .from("organizationInvites")
        .update({
          status: "declined",
        })
        .eq("id", inviteId);

      if (error) throw error;
      navigate("/");
      refreshInvites(); // Refresh notifications after declining
    } catch (error) {
      console.error("Error declining invite:", error);
      setActionError("Failed to decline invitation. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  const handleSignupSuccess = async (signupData) => {
    setSignupSuccess(true);

    // Refresh organizations and notifications
    await refreshOrganizations();
    refreshInvites();

    // Show success message - user will be redirected after email verification
    // The auth callback will handle accepting the invite and redirecting to the organization
  };

  const handleSignupError = (errorMessage) => {
    setActionError(errorMessage);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <SkeletonBoard rows={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="max-w-md w-full card-board p-8 text-center">
          <FaTimes className="text-red-500 text-4xl mx-auto mb-4" />
          <h1 className="heading-display text-xl mb-2">
            Invalid Invitation
          </h1>
          <p className="text-brand-muted mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="bg-brand-cream text-brand-charcoal px-4 py-2 rounded-lg border border-brand-charcoal/20 hover:border-brand-gold-ink font-display font-bold tracking-wide"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
      <div className="max-w-md w-full card-board p-8">
        <div className="text-center mb-6">
          <div className="mx-auto h-16 w-16 bg-brand-cream rounded-full flex items-center justify-center mb-4">
            <FaBuilding className="h-8 w-8 text-brand-gold-ink" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Organization Invitation
          </h1>
          <p className="text-gray-600">
            You've been invited to join an organization
          </p>
          {actionError ? (
            <div className="mb-3">
              <InlineAlert
                type="error"
                title="There was a problem"
                message={actionError}
                onClose={() => setActionError("")}
              />
            </div>
          ) : null}
        </div>

        <div className="card-inset p-4 mb-6">
          <h2 className="font-semibold text-gray-900 mb-2">
            {organization.name}
            {organization.nameAbbreviation && (
              <span className="text-gray-500 ml-2">
                ({organization.nameAbbreviation})
              </span>
            )}
          </h2>
          {organization.description && (
            <p className="text-gray-600 text-sm mb-3">
              {organization.description}
            </p>
          )}
          <p className="text-sm text-gray-500">
            Invitation sent by:{" "}
            <span className="font-medium">
              {invite.invitedByUser?.email || "Unknown User"}
            </span>
          </p>
        </div>

        {signupSuccess ? (
          <div className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 bg-brand-charcoal rounded-lg flex items-center justify-center mb-4">
              <FaCheck className="h-8 w-8 text-brand-cream" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Account Created Successfully!
            </h2>
            <p className="text-gray-600">
              Your account has been created. Please check your email to verify
              your account and join {organization.name}.
            </p>
            <p className="text-sm text-gray-500">
              After verifying your email, you'll be automatically added to the
              organization.
            </p>
          </div>
        ) : showSignupForm ? (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="mx-auto h-12 w-12 bg-brand-cream rounded-full flex items-center justify-center mb-3">
                <FaUserPlus className="h-6 w-6 text-brand-gold-ink" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Create Your Account
              </h3>
              <p className="text-sm text-gray-600">
                You've been invited to join {organization.name}. Create an
                account to get started.
              </p>
            </div>
            {actionError && (
              <InlineAlert
                type="error"
                title="There was a problem"
                message={actionError}
                onClose={() => setActionError("")}
              />
            )}
            <InviteSignupForm
              invite={invite}
              onSuccess={handleSignupSuccess}
              onError={handleSignupError}
            />
            <div className="text-center">
              <p className="text-sm text-gray-500">
                Already have an account?{" "}
                <button
                  onClick={() =>
                    navigate("/signin", {
                      state: { from: `/invite/${inviteId}` },
                    })
                  }
                  className="text-brand-gold-ink hover:text-brand-charcoal font-medium"
                >
                  Sign in instead
                </button>
              </p>
            </div>
          </div>
        ) : user && userStatus?.userExists && !userStatus?.needsSignIn ? (
          <div className="space-y-3">
            <div className="text-center mb-4">
              <div className="mx-auto h-12 w-12 bg-brand-charcoal rounded-lg flex items-center justify-center mb-3">
                <FaCheck className="h-6 w-6 text-brand-cream" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Welcome, {user.user_metadata?.display_name || user.email}!
              </h3>
              <p className="text-sm text-gray-600">
                You're signed in and ready to accept this invitation to join{" "}
                {organization.name}.
              </p>
            </div>
            {actionError && (
              <InlineAlert
                type="error"
                title="There was a problem"
                message={actionError}
                onClose={() => setActionError("")}
              />
            )}
            <button
              onClick={handleAcceptInvite}
              disabled={accepting}
              className="w-full bg-brand-gold text-brand-charcoal py-2 px-4 rounded-lg hover:bg-brand-gold/90 font-display font-bold tracking-wide disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {accepting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Accepting...
                </>
              ) : (
                <>
                  <FaCheck className="mr-2" />
                  Accept Invitation
                </>
              )}
            </button>
            <button
              onClick={handleDeclineInvite}
              disabled={accepting}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {accepting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Declining...
                </>
              ) : (
                <>
                  <FaTimes className="mr-2" />
                  Decline Invitation
                </>
              )}
            </button>
          </div>
        ) : userStatus?.needsSignIn ? (
          <div className="space-y-3">
            <div className="text-center mb-4">
              <div className="mx-auto h-12 w-12 bg-brand-cream rounded-full flex items-center justify-center mb-3">
                <FaUserPlus className="h-6 w-6 text-brand-gold-ink" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Account Already Exists
              </h3>
              <p className="text-sm text-gray-600">
                An account with this email already exists. Please sign in to
                accept the invitation.
              </p>
            </div>
            <button
              onClick={() =>
                navigate("/signin", { state: { from: `/invite/${inviteId}` } })
              }
              className="w-full bg-brand-gold text-brand-charcoal py-2 px-4 rounded-lg hover:bg-brand-gold/90 font-display font-bold tracking-wide"
            >
              Sign In
            </button>
            <p className="text-xs text-gray-500 text-center">
              Don't remember your password?{" "}
              <button
                onClick={() => navigate("/forgot-password")}
                className="text-brand-gold-ink hover:text-brand-charcoal font-medium"
              >
                Reset it here
              </button>
            </p>
          </div>
        ) : !user ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 text-center">
              You need to sign in to accept this invitation
            </p>
            <button
              onClick={() =>
                navigate("/signin", { state: { from: `/invite/${inviteId}` } })
              }
              className="w-full bg-brand-gold text-brand-charcoal py-2 px-4 rounded-lg hover:bg-brand-gold/90 font-display font-bold tracking-wide"
            >
              Sign In
            </button>
            <button
              onClick={() =>
                navigate("/signup", { state: { from: `/invite/${inviteId}` } })
              }
              className="w-full bg-brand-gold text-brand-charcoal py-2 px-4 rounded-lg hover:bg-brand-gold/90 font-display font-bold tracking-wide"
            >
              Create Account
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={handleAcceptInvite}
              disabled={accepting}
              className="w-full bg-brand-gold text-brand-charcoal py-2 px-4 rounded-lg hover:bg-brand-gold/90 font-display font-bold tracking-wide disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {accepting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Accepting...
                </>
              ) : (
                <>
                  <FaCheck className="mr-2" />
                  Accept Invitation
                </>
              )}
            </button>
            <button
              onClick={handleDeclineInvite}
              disabled={accepting}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {accepting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Declining...
                </>
              ) : (
                <>
                  <FaTimes className="mr-2" />
                  Decline Invitation
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
